import { useMemo } from 'react'
import { useQuery } from 'react-query'

import { query } from '../api'
import {
  deleteAutomationOf,
  lockedTalentSpacesFor,
  lockedTalentsFor,
  summarizeUsage
} from '../helper/pricing'

// Nutzung eines Kunden für Paket-Check, Kundenseite und Migrations-Übersicht.
// Gezählt wird mit den Regeln aus src/helper/pricing-mirror, also wie im
// Dashboard. Nicht getUsageReport nutzen, der zählt archivierte Stellen mit
// und kennt soft_deleted nicht.

export const PRICING_USAGE_STALE_TIME = 10 * 60 * 1000

export const pricingUsageQueryKey = (partnerId) => ['pricing-usage', partnerId]

// Dieselben Kontexte wie im Dashboard (graphql/filter-inputs.js), Talente
// und Zugänge in einem Durchgang. Den Rest entscheiden die Zählregeln.
export const CAREER_ACTIVE_FILTER = { status: { eq: 'ACTIVE' } }
export const TALENT_CONTEXTS = ['TALENTPOOL', 'SELECT', 'ATTRACT']
export const SEAT_CONTEXT = 'EMPLOYEE'
export const SPACE_USAGE_FILTER = {
  or: [...TALENT_CONTEXTS, SEAT_CONTEXT].map((context) => ({
    context: { eq: context }
  }))
}

const listOrThrow = (list, what) => {
  // Ohne Daten gilt die Zählung als fehlgeschlagen und nicht als 0
  if (!Array.isArray(list)) {
    throw new Error(`Nutzung (${what}) konnte nicht geladen werden`)
  }
  return list
}

/**
 * Teilt die Spaces eines Partners in Talente (wie die Talentliste im
 * Dashboard) und Zugänge (EMPLOYEE). Andere Kontexte fallen weg, damit der
 * Rang des Talentspeichers dieselben Zeilen sieht wie das Dashboard.
 */
export const splitSpaces = (spaces) => {
  const talents = []
  const members = []
  ;(Array.isArray(spaces) ? spaces : []).forEach((space) => {
    if (!space) return
    if (space.context === SEAT_CONTEXT) members.push(space)
    else if (TALENT_CONTEXTS.includes(space.context)) talents.push(space)
  })
  return { talents, members }
}

/**
 * Lädt Stellen und Spaces eines Partners und zählt sie. Die Space-Partition
 * wird genau einmal gelesen.
 *
 * @returns {Promise<{ careers: number, seats: number, talentStorage: number,
 *   index: object }>} index ist der Rang des Talentspeichers
 *   (rankTalentStorage)
 */
export const fetchPricingUsage = async (partnerId) => {
  if (!partnerId) throw new Error('Nutzung: partnerId fehlt')

  const [careers, spaces] = await Promise.all([
    query({
      query: 'careerUsageByPartnerByDate',
      variables: { partner_id: partnerId, filter: CAREER_ACTIVE_FILTER }
    }),
    query({
      query: 'spaceUsageByPartner',
      variables: { partner_id: partnerId, filter: SPACE_USAGE_FILTER }
    })
  ])

  const { talents, members } = splitSpaces(
    listOrThrow(spaces, 'Talente und Zugänge')
  )

  return summarizeUsage({
    careers: listOrThrow(careers, 'Stellen'),
    spaces: talents,
    members
  })
}

// Für queryClient.fetchQuery, etwa „Alle prüfen" in der Migrations-Übersicht
export const pricingUsageQueryOptions = (partnerId) => ({
  queryKey: pricingUsageQueryKey(partnerId),
  queryFn: () => fetchPricingUsage(partnerId),
  staleTime: PRICING_USAGE_STALE_TIME
})

/**
 * Nutzung aus den Query-Daten. lockedTalents(limit) ist die Zahl der
 * Personen, die bei diesem Talentspeicher gesperrt wären,
 * lockedTalentSpaces(limit) die Zahl ihrer Zeilen in der Talentliste.
 * Unbegrenzt, unbekannt oder ungültig sperrt nichts.
 */
export const toPricingUsage = (data) =>
  data
    ? {
        careers: data.careers,
        seats: data.seats,
        talentStorage: data.talentStorage,
        lockedTalents: (limit) => lockedTalentsFor(data.index, limit),
        lockedTalentSpaces: (limit) => lockedTalentSpacesFor(data.index, limit)
      }
    : null

/**
 * @param {string} partnerId
 * @param {{ enabled?: boolean }} [options] enabled false lädt erst auf Abruf
 * @returns {{ usage: { careers: number, seats: number, talentStorage: number,
 *   lockedTalents: (limit: number) => number,
 *   lockedTalentSpaces: (limit: number) => number }|null,
 *   isLoading: boolean, isFetching: boolean, isError: boolean,
 *   error: unknown, refetch: () => Promise<unknown> }}
 */
export const usePricingUsage = (partnerId, { enabled = true } = {}) => {
  const { data, isLoading, isFetching, isError, error, refetch } = useQuery({
    ...pricingUsageQueryOptions(partnerId),
    enabled: !!partnerId && enabled
  })

  const usage = useMemo(() => toPricingUsage(data), [data])

  return { usage, isLoading, isFetching, isError, error, refetch }
}

export const partnerAutomationsQueryKey = (partnerId) => [
  'partner-automations',
  partnerId
]

/**
 * DSGVO-Automatisierung des Partners (app_settings.automations) für die
 * Warnung auf der Payment-Seite. deleteAutomation ist null, wenn nicht
 * gelöscht wird.
 *
 * @returns {{ partner: object|null, deleteAutomation: { option: string,
 *   months: number }|null, isLoading: boolean, isError: boolean }}
 */
export const usePartnerAutomations = (partnerId, { enabled = true } = {}) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: partnerAutomationsQueryKey(partnerId),
    queryFn: () =>
      query({ query: 'getPartnerAutomations', variables: { id: partnerId } }),
    enabled: !!partnerId && enabled,
    staleTime: PRICING_USAGE_STALE_TIME
  })

  const deleteAutomation = useMemo(
    () => (data ? deleteAutomationOf(data) : null),
    [data]
  )

  return { partner: data || null, deleteAutomation, isLoading, isError }
}
